import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import type { ParcoursStep } from "../modules/academy/parcours/models/ParcoursStep";
import type { UserProgress } from "../modules/academy/progression.types";

type ParcoursProgressState = UserProgress["parcoursProgress"];

interface ParcoursContextValue {
  parcoursProgress: ParcoursProgressState;
  isGuest: boolean;
  startParcours: (parcoursId: string, firstStepId?: string) => void;
  markStepCompleted: (parcoursId: string, stepId: string, steps?: ParcoursStep[]) => void;
  getCurrentStep: (parcoursId: string, steps: ParcoursStep[]) => ParcoursStep | null;
  getNextStep: (parcoursId: string, steps: ParcoursStep[]) => ParcoursStep | null;
  isStepCompleted: (parcoursId: string, stepId: string) => boolean;
}

const STORAGE_KEY = "mixmind_parcours_progress_v1";
const ParcoursContext = createContext<ParcoursContextValue | undefined>(undefined);

const defaultParcoursProgress: ParcoursProgressState = {};

function safeLoad(): ParcoursProgressState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultParcoursProgress };
    const parsed = JSON.parse(raw) as ParcoursProgressState;
    return parsed || { ...defaultParcoursProgress };
  } catch (e) {
    console.error("Failed to parse parcours progress", e);
    return { ...defaultParcoursProgress };
  }
}

export function ParcoursProvider({ children }: { children: React.ReactNode }) {
  const { isAuthed } = useAuth();
  const isGuest = !isAuthed;
  const [parcoursProgress, setParcoursProgress] = useState<ParcoursProgressState>({ ...defaultParcoursProgress });

  useEffect(() => {
    if (isGuest) {
      setParcoursProgress({ ...defaultParcoursProgress });
      return;
    }
    setParcoursProgress(safeLoad());
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parcoursProgress));
    } catch (e) {
      console.error("Failed to persist parcours progress", e);
    }
  }, [parcoursProgress, isGuest]);

  const startParcours = useCallback(
    (parcoursId: string, firstStepId?: string) => {
      if (isGuest) return;
      setParcoursProgress((prev) => {
        const existing = prev[parcoursId];
        if (existing) return prev;
        return {
          ...prev,
          [parcoursId]: {
            completedSteps: [],
            currentStepId: firstStepId,
            started: true,
          },
        };
      });
    },
    [isGuest]
  );

  const markStepCompleted = useCallback(
    (parcoursId: string, stepId: string, steps?: ParcoursStep[]) => {
      if (isGuest) return;
      setParcoursProgress((prev) => {
        const base = prev[parcoursId] || { completedSteps: [], started: true };
        const completedSteps = base.completedSteps.includes(stepId)
          ? base.completedSteps
          : [...base.completedSteps, stepId];

        let nextCurrent = base.currentStepId;
        if (steps && base.currentStepId === stepId) {
          const next = steps.find((s) => !completedSteps.includes(s.id));
          nextCurrent = next ? next.id : undefined;
        }

        return {
          ...prev,
          [parcoursId]: {
            completedSteps,
            started: true,
            currentStepId: nextCurrent,
          },
        };
      });
    },
    [isGuest]
  );

  const getCurrentStep = useCallback(
    (parcoursId: string, steps: ParcoursStep[]) => {
      const state = parcoursProgress[parcoursId];
      if (!state) return steps[0] || null;
      if (state.currentStepId) {
        const found = steps.find((s) => s.id === state.currentStepId);
        if (found) return found;
      }
      const next = steps.find((s) => !state.completedSteps.includes(s.id));
      return next || null;
    },
    [parcoursProgress]
  );

  const getNextStep = useCallback(
    (parcoursId: string, steps: ParcoursStep[]) => {
      const state = parcoursProgress[parcoursId];
      if (!state) return steps[0] || null;
      const next = steps.find((s) => !state.completedSteps.includes(s.id));
      return next || null;
    },
    [parcoursProgress]
  );

  const isStepCompleted = useCallback(
    (parcoursId: string, stepId: string) => {
      const state = parcoursProgress[parcoursId];
      if (!state) return false;
      return state.completedSteps.includes(stepId);
    },
    [parcoursProgress]
  );

  const value = useMemo<ParcoursContextValue>(
    () => ({
      parcoursProgress,
      isGuest,
      startParcours,
      markStepCompleted,
      getCurrentStep,
      getNextStep,
      isStepCompleted,
    }),
    [parcoursProgress, isGuest, startParcours, markStepCompleted, getCurrentStep, getNextStep, isStepCompleted]
  );

  return <ParcoursContext.Provider value={value}>{children}</ParcoursContext.Provider>;
}

export function useParcours() {
  const ctx = useContext(ParcoursContext);
  if (!ctx) throw new Error("useParcours must be used within ParcoursProvider");
  return ctx;
}
